import { GLOBAL_SEO_REGISTRY, getSeoEntryByRoute } from "./seoRegistry";
import { getRawSeoConfig } from "./seoConfig";
import { getBreadcrumbNodes } from "./breadcrumbRegistry";
import { getLocationBySlug, isLocationQualified, getAllQualifiedLocations } from "./locationRegistry";
import { getIndustryBySlug, getAllIndustries } from "./industryRegistry";
import { getGuideBySlug, getAllGuides } from "./guideRegistry";
import { getFeatureBySlug, getAllFeatures } from "./featureRegistry";
import { getReportBySlug, getAllReports } from "./reportRegistry";
import { getMethodologyArticleBySlug, getAllMethodologyArticles } from "./methodologyRegistry";
import { getCompareArticleBySlug, getAllCompareArticles } from "./compareRegistry";

const PRIMARY_DOMAIN = "https://getbiznearyou.com";

export function isSearchCrawler(userAgent: any): boolean {
  if (!userAgent) return false;
  const rawStr = Array.isArray(userAgent) ? userAgent.join(" ") : String(userAgent);
  const lower = rawStr.toLowerCase();
  return (
    lower.includes("googlebot") ||
    lower.includes("bingbot") ||
    lower.includes("slurp") ||
    lower.includes("duckduckbot") ||
    lower.includes("baiduspider") ||
    lower.includes("yandexbot") ||
    lower.includes("sogou") ||
    lower.includes("exabot") ||
    lower.includes("facebot") ||
    lower.includes("facebookexternalhit") ||
    lower.includes("twitterbot") ||
    lower.includes("linkedinbot") ||
    lower.includes("pinterest") ||
    lower.includes("bot") ||
    lower.includes("crawler") ||
    lower.includes("spider")
  );
}

export function getRootInjectionHtml(targetPath: string, userAgent: any): string {
  const loadingShell = `
    <div id="app-loading-shell" style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0f172a;color:#94a3b8;font-family:sans-serif;padding:2rem;">
      <style>@keyframes biz-spin { to { transform: rotate(360deg); } }</style>
      <div style="text-align:center;max-width:320px;">
        <div style="width:44px;height:44px;border:3px solid #1e293b;border-top-color:#14b8a6;border-radius:50%;animation:biz-spin 0.75s linear infinite;margin:0 auto 16px auto;"></div>
        <div style="font-size:15px;font-weight:700;color:#f1f5f9;letter-spacing:-0.01em;">Loading BizNearYou...</div>
        <div style="font-size:12px;color:#64748b;margin-top:6px;">Connecting local market intelligence</div>
      </div>
    </div>
  `.trim();

  try {
    const isBot = isSearchCrawler(userAgent);
    const safePath = typeof targetPath === "string" ? targetPath : "/";
    const cleanPath = safePath.split("?")[0].split("#")[0] || "/";
    const isPrivateOrAuth = ["/dashboard", "/app", "/settings", "/saved", "/account", "/login", "/signup", "/billing", "/auth"].some(
      prefix => cleanPath === prefix || cleanPath.startsWith(prefix + "/")
    );

    // On private or auth routes, never render public SEO fallback text
    if (isPrivateOrAuth) {
      return `<div id="root">${loadingShell}</div>`;
    }

    const preRenderedBody = generatePreRenderedBodyHtml(cleanPath);

    // If request is from an SEO bot/crawler, give direct pre-rendered HTML
    if (isBot) {
      return `<div id="root">${preRenderedBody}</div>`;
    }

    // For human browsers on public routes, show loading shell while JS loads + noscript fallback for crawlability
    return `<div id="root">${loadingShell}<noscript>${preRenderedBody}</noscript></div>`;
  } catch (err) {
    console.error("[SSR Injection Failure] Exception in getRootInjectionHtml:", err);
    return `<div id="root">${loadingShell}</div>`;
  }
}

/**
 * Generates semantic pre-rendered HTML fallback for the <div id="root"> container.
 * Ensures search crawlers and Googlebot see rich, structured text, H1 tags, breadcrumbs,
 * and crawlable internal links without executing JavaScript.
 */
export function generatePreRenderedBodyHtml(targetPath: string): string {
  try {
    const cleanPath = targetPath ? targetPath.split("?")[0].split("#")[0] || "/" : "/";
    const seoConfig = getRawSeoConfig(cleanPath);
    const breadcrumbNodes = getBreadcrumbNodes(cleanPath);

    // Build Breadcrumb HTML
    let breadcrumbHtml = "";
    if (breadcrumbNodes && breadcrumbNodes.length > 0) {
      const itemsHtml = breadcrumbNodes.map((node, index) => {
        const isLast = index === breadcrumbNodes.length - 1;
        const fullUrl = node.path.startsWith("http") ? node.path : `${PRIMARY_DOMAIN}${node.path.startsWith("/") ? "" : "/"}${node.path}`;
        if (isLast) {
          return `<li aria-current="page"><span>${escapeHtml(node.label)}</span></li>`;
        }
        return `<li><a href="${escapeHtml(fullUrl)}">${escapeHtml(node.label)}</a> &rsaquo; </li>`;
      }).join("");
      breadcrumbHtml = `<nav aria-label="Breadcrumb" class="ssr-breadcrumbs"><ol style="display:flex;flex-wrap:wrap;gap:0.5rem;list-style:none;padding:0;margin:0 0 1rem 0;font-size:0.875rem;color:#64748b;">${itemsHtml}</ol></nav>`;
    }

    // Derive H1, lead paragraph, and page section content
    const regEntry = getSeoEntryByRoute(cleanPath);
    const h1Text = regEntry?.h1 || deriveH1Title(cleanPath, seoConfig?.title || "BizNearYou");
    const leadText = regEntry?.metaDescription || seoConfig?.description || "Find local businesses and employment opportunities near you.";

    // Generate specific content blocks based on route type
    const bodyContentHtml = buildRouteSpecificContentHtml(cleanPath, regEntry);
    const internalLinksHtml = buildCrawlableInternalLinksHtml(cleanPath);

    return `
      <div id="ssr-app-fallback" class="min-h-screen bg-slate-900 text-slate-100 font-sans antialiased">
        <nav class="border-b border-slate-800 bg-slate-950/80 backdrop-blur px-4 py-3 sticky top-0 z-50">
          <div class="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <a href="/" class="flex items-center gap-2 text-teal-400 font-extrabold text-xl tracking-tight hover:text-teal-300">
              <span class="w-8 h-8 rounded-lg bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-400 text-sm font-black">B</span>
              BizNearYou
            </a>
            <div class="hidden sm:flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-400 w-64">
              <span class="text-slate-500">Search local businesses...</span>
            </div>
            <div class="flex items-center gap-3">
              <a href="/login" class="text-xs font-semibold text-slate-300 hover:text-white px-3 py-1.5 rounded-lg border border-slate-800 bg-slate-900/60">Log In</a>
              <a href="/signup" class="text-xs font-semibold text-white bg-teal-600 hover:bg-teal-500 px-3 py-1.5 rounded-lg shadow-sm">Get Started</a>
            </div>
          </div>
        </nav>

        <div class="max-w-7xl mx-auto px-4 py-8">
          <header class="mb-8 pb-6 border-b border-slate-800">
            ${breadcrumbHtml}
            <h1 class="text-3xl sm:text-4xl font-extrabold text-slate-100 tracking-tight mb-3">${escapeHtml(h1Text)}</h1>
            <p class="text-lg text-slate-300 max-w-3xl leading-relaxed">${escapeHtml(leadText)}</p>
          </header>

          <main class="mb-12">
            ${bodyContentHtml}
          </main>

          <footer class="mt-12 pt-8 border-t border-slate-800 text-slate-400">
            <nav aria-label="Explore Related Pages">
              <h2 class="text-xl font-bold text-slate-200 mb-4">Explore BizNearYou Directories & Resources</h2>
              ${internalLinksHtml}
            </nav>
          </footer>
        </div>
      </div>
    `.trim();
  } catch (err) {
    console.error("[SSR PreRender Failure] Exception in generatePreRenderedBodyHtml:", err);
    return `
      <div id="ssr-app-fallback">
        <header><h1>BizNearYou - Local Business & Hiring Search</h1></header>
        <p>Find local businesses and unadvertised job opportunities near you.</p>
      </div>
    `.trim();
  }
}

function deriveH1Title(path: string, pageTitle: string): string {
  if (path === "/") return "Find Local Businesses Showing Active Hiring Signals";
  if (path === "/about") return "About BizNearYou: Unposted Local Opportunity Intelligence";
  if (path === "/pricing") return "BizNearYou Pricing & Access Plans";
  if (path === "/how-it-works") return "How BizNearYou Signal Intelligence Works";
  if (path === "/find-jobs") return "Discover Local Employers & Unposted Job Opportunities";
  if (path === "/hidden-jobs") return "Find Unadvertised Local Jobs Before Postings Go Live";

  const cleanTitle = pageTitle.replace(/\s*\|\s*BizNearYou$/i, "");
  return cleanTitle;
}

function buildRouteSpecificContentHtml(path: string, regEntry: any): string {
  if (regEntry) {
    let sections = "";
    if (regEntry.directAnswer) {
      sections += `<section style="margin-bottom:2rem;"><h2 style="font-size:1.5rem;font-weight:700;color:#0f172a;margin-bottom:0.75rem;">Quick Answer</h2><p style="color:#334155;font-size:1.05rem;line-height:1.6;font-weight:500;">${escapeHtml(regEntry.directAnswer)}</p></section>`;
    }
    if (regEntry.overview) {
      sections += `<section style="margin-bottom:2rem;"><h2 style="font-size:1.5rem;font-weight:700;color:#0f172a;margin-bottom:0.75rem;">Overview</h2><p style="color:#334155;font-size:1rem;line-height:1.6;">${escapeHtml(regEntry.overview)}</p></section>`;
    }
    if (regEntry.keyTakeaways && Array.isArray(regEntry.keyTakeaways) && regEntry.keyTakeaways.length > 0) {
      const items = regEntry.keyTakeaways.map((t: string) => `<li style="margin-bottom:0.5rem;color:#334155;">${escapeHtml(t)}</li>`).join("");
      sections += `<section style="margin-bottom:2rem;"><h2 style="font-size:1.35rem;font-weight:700;color:#0f172a;margin-bottom:0.75rem;">Key Takeaways</h2><ul style="padding-left:1.5rem;margin:0;line-height:1.6;">${items}</ul></section>`;
    }
    if (regEntry.contentSections && Array.isArray(regEntry.contentSections) && regEntry.contentSections.length > 0) {
      for (const sec of regEntry.contentSections) {
        const bodyText = sec.body ? sec.body : (Array.isArray(sec.paragraphs) ? sec.paragraphs.join(" ") : "");
        if (bodyText) {
          sections += `<section style="margin-bottom:2rem;"><h2 style="font-size:1.35rem;font-weight:700;color:#0f172a;margin-bottom:0.75rem;">${escapeHtml(sec.heading)}</h2><p style="color:#334155;line-height:1.6;font-size:1rem;">${escapeHtml(bodyText)}</p></section>`;
        }
      }
    }
    if (sections) return sections;
  }

  // Location pages
  const locMatch = path.match(/^\/(jobs|businesses-hiring|internships)\/([a-z0-9-]+)$/);
  if (locMatch) {
    const rawSlug = locMatch[2];
    const loc = getLocationBySlug(rawSlug);
    if (loc && isLocationQualified(loc)) {
      return `
        <article style="display:grid;gap:1.5rem;">
          <section>
            <h2 style="font-size:1.5rem;font-weight:700;color:#0f172a;">Local Commercial Market Overview for ${escapeHtml(loc.cityName)}, ${escapeHtml(loc.stateAbbreviation)}</h2>
            <p style="color:#334155;margin-top:0.5rem;">
              ${escapeHtml(loc.cityName)} (${escapeHtml(loc.stateName)}) represents an active regional business ecosystem with a total estimated 
              <strong>${loc.businessCount ? loc.businessCount.toLocaleString() : "50+"}</strong> tracked commercial employers and practices. BizNearYou tracks local commercial indicators, active medical practices, engineering suppliers, trade contractors, and private professional service firms across ${escapeHtml(loc.cityName)} to identify businesses experiencing commercial growth before official job openings are posted on external job boards.
            </p>
          </section>

          <section>
            <h3 style="font-size:1.25rem;font-weight:700;color:#0f172a;">Hiring Signal Breakdown in ${escapeHtml(loc.cityName)}</h3>
            <p style="color:#334155;margin-top:0.5rem;">
              Our commercial detection pipeline monitors local commercial domain registrations, verified customer review activity, expansion notices, and hiring indicators. Job seekers and local outreach professionals use these public signals to pitch business owners directly in ${escapeHtml(loc.cityName)}, ${escapeHtml(loc.stateAbbreviation)}.
            </p>
          </section>

          <section>
            <h3 style="font-size:1.25rem;font-weight:700;color:#0f172a;">How to Pitch Businesses in ${escapeHtml(loc.cityName)}</h3>
            <ol style="padding-left:1.5rem;color:#334155;line-height:1.7;">
              <li>Identify high-opportunity local practices, contracting firms, or agencies exhibiting recent growth signals.</li>
              <li>Research the business owner, office administrator, or decision-maker directly.</li>
              <li>Reach out with a short, tailored proposal emphasizing how your background solves immediate local operational needs.</li>
            </ol>
          </section>
        </article>
      `;
    }
  }

  // Fallback default article body
  return `
    <article>
      <section style="margin-bottom:2rem;">
        <h2 style="font-size:1.5rem;font-weight:700;color:#0f172a;margin-bottom:0.75rem;">Local Commercial Intelligence & Opportunity Discovery</h2>
        <p style="color:#334155;line-height:1.6;">
          BizNearYou provides real-time visibility into local business growth, commercial expansion signals, and unadvertised employment opportunities across the United States, Canada, Mexico, and international regional hubs.
        </p>
      </section>

      <section style="margin-bottom:2rem;">
        <h2 style="font-size:1.35rem;font-weight:700;color:#0f172a;margin-bottom:0.75rem;">Why Direct Owner Outreach Outperforms Job Boards</h2>
        <p style="color:#334155;line-height:1.6;">
          Much of hiring in small-to-medium businesses, private medical practices, specialized contractors, and local service agencies occurs before a formal job posting is created. By tracking public commercial growth indicators, candidates and outreach specialists can contact managers directly before competition multiplies.
        </p>
      </section>
    </article>
  `;
}

function buildCrawlableInternalLinksHtml(currentPath: string): string {
  const topLocations = getAllQualifiedLocations()
    .filter(loc => loc.countryCode === "US")
    .slice(0, 16);

  const locLinks = topLocations.map(loc => 
    `<li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/jobs/${loc.citySlug}" style="color:#2563eb;text-decoration:underline;">${escapeHtml(loc.cityName)}, ${escapeHtml(loc.stateAbbreviation)} Jobs</a></li>`
  ).join("");

  const audienceLinks = `
    <li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/for-job-seekers" style="color:#2563eb;text-decoration:underline;">For Job Seekers</a></li>
    <li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/for-recruiters" style="color:#2563eb;text-decoration:underline;">For Recruiters</a></li>
    <li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/for-staffing-agencies" style="color:#2563eb;text-decoration:underline;">For Staffing Agencies</a></li>
    <li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/for-career-coaches" style="color:#2563eb;text-decoration:underline;">For Career Coaches</a></li>
  `;

  const resourceLinks = `
    <li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/about" style="color:#2563eb;text-decoration:underline;">About BizNearYou</a></li>
    <li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/pricing" style="color:#2563eb;text-decoration:underline;">Pricing & Access</a></li>
    <li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/how-it-works" style="color:#2563eb;text-decoration:underline;">How It Works</a></li>
    <li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/hiring-signal-methodology" style="color:#2563eb;text-decoration:underline;">Hiring Signal Methodology</a></li>
    <li style="margin-bottom:0.4rem;"><a href="${PRIMARY_DOMAIN}/jobs/locations" style="color:#2563eb;text-decoration:underline;">Job Locations Directory</a></li>
  `;

  return `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:1.5rem;font-size:0.9rem;">
      <div>
        <h3 style="font-size:1rem;font-weight:700;color:#334155;margin-bottom:0.5rem;">Featured Job Hubs</h3>
        <ul style="list-style:none;padding:0;margin:0;">${locLinks}</ul>
      </div>
      <div>
        <h3 style="font-size:1rem;font-weight:700;color:#334155;margin-bottom:0.5rem;">Solutions by Audience</h3>
        <ul style="list-style:none;padding:0;margin:0;">${audienceLinks}</ul>
      </div>
      <div>
        <h3 style="font-size:1rem;font-weight:700;color:#334155;margin-bottom:0.5rem;">Core Platform Resources</h3>
        <ul style="list-style:none;padding:0;margin:0;">${resourceLinks}</ul>
      </div>
    </div>
  `;
}

function escapeHtml(str: string): string {
  if (!str) return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
